import { aiToolRegistry, getAiTool } from '../aiToolRegistry';

const DEFAULT_NATIVE_TOOL_ICON = 'hammer';
const DEFAULT_MCP_SERVER_ICON = 'mcp';
/** Composio tools use the same `prefix__slug` key shape as MCP but are not MCP servers. */
const DEFAULT_COMPOSIO_TOOL_ICON = 'light-composio';

export type McpServerIconLookup = {
  name: string;
  icon?: string;
};

export type McpServerIconOverride = {
  icon?: string;
};

export type McpServerIconResolver = (
  serverName: string,
  override?: McpServerIconOverride,
) => string;

export type GetToolIconOptions = {
  mcpServers?: ReadonlyArray<McpServerIconLookup>;
};

let mcpServerIconResolver: McpServerIconResolver | null = null;

/** Registers the resolver used for MCP server icons when no explicit override is provided. */
export function registerMcpServerIconResolver(resolver: McpServerIconResolver): void {
  mcpServerIconResolver = resolver;
}

/** Clears the MCP server icon resolver (for unit tests). */
export function clearMcpServerIconResolver(): void {
  mcpServerIconResolver = null;
}

/**
 * Parses an MCP-prefixed tool key (`serverName__toolName`) into its parts.
 * Returns null when the key is not MCP-shaped.
 */
export function parseMcpToolKey(toolKey: string): { serverName: string; shortName: string } | null {
  const separatorIndex = toolKey.indexOf('__');
  if (separatorIndex <= 0) {
    return null;
  }

  const serverName = toolKey.slice(0, separatorIndex);
  const shortName = toolKey.slice(separatorIndex + 2);
  if (!shortName) {
    return null;
  }

  return { serverName, shortName };
}

function iconFromNativeGroup(groupKey: string): string {
  const iconSource = aiToolRegistry
    .filter((tool) => tool.group === groupKey && tool.icon !== undefined)
    .sort((a, b) => a.id.localeCompare(b.id))[0];

  return iconSource?.icon ?? DEFAULT_NATIVE_TOOL_ICON;
}

function isConnectionToolPrefix(prefix: string): boolean {
  return prefix === 'connection' || prefix.startsWith('connection-');
}

/**
 * Resolves the Font Awesome icon name for a native AiTool id or MCP/Composio tool key
 * (`serverName__toolName`). Icons render with Font Awesome Light (`fal`).
 * Composio chat label fallbacks reuse `_mcp*` keys in `translateAiTool` (same `__` shape).
 */
export function getToolIcon(toolKey: string, options?: GetToolIconOptions): string {
  const mcpParts = parseMcpToolKey(toolKey);
  if (mcpParts) {
    const override = options?.mcpServers?.find((server) => server.name === mcpParts.serverName);
    if (override?.icon) {
      return override.icon;
    }
    if (isConnectionToolPrefix(mcpParts.serverName)) {
      return DEFAULT_COMPOSIO_TOOL_ICON;
    }
    if (mcpServerIconResolver !== null) {
      return mcpServerIconResolver(mcpParts.serverName, override);
    }
    return DEFAULT_MCP_SERVER_ICON;
  }

  const aiTool = getAiTool(toolKey);
  if (aiTool?.icon) {
    return aiTool.icon;
  }

  if (aiTool) {
    return iconFromNativeGroup(aiTool.group);
  }

  return DEFAULT_NATIVE_TOOL_ICON;
}
